import React, { Suspense, useState } from 'react';
import Card from '@/components/card';
import BreadcrumbComponent from '@/components/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import { IOrder } from '@/interfaces/order';
import { formatDate } from '@/utils/dataUtils';
import { DetailItem } from '@/components/dataItem/data-view-item';
import { getFullAddress } from '@/utils/customerUtils';
import Title from '@/components/title';
import OrderItemTable from '@/containers/order/maintain/components/order-info/components/order-item-table';
import { ICndn } from '@/interfaces/cndn';
import { getOrder } from '@/adapters/order';
import { deleteCndn, getCndn } from '@/adapters/cndn';
import CndnItemTable from './components/cndn-item-table';
import { useQuery } from '@tanstack/react-query';
import { Button, Divider, notification } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import PrintCndnModal from '@/components/templates/print-cndn-modal';
import { getCndnTypeName } from '@/helpers/cndnHelper';

const _cndnModal: IDataModal<ICndn> = {
  open: false,
};

const _printModal: IDataModal<ICndn> = {
  open: false,
};

const ViewCndn = () => {
  const navigate = useNavigate();

  const { id } = useParams();
  const { data: cndn, refetch } = useQuery<ICndn, Error>({
    queryKey: ['get-cndn', id],
    queryFn: async () => await getCndn(Number(id)),
  });

  const { data: order } = useQuery<IOrder, Error>({
    queryKey: ['get-order', cndn?.orderId],
    queryFn: async () => await getOrder(Number(cndn?.orderId)),
    enabled: !!cndn?.orderId,
  });

  const [deleteModal, setDeleteModal] = useState<IDataModal<ICndn>>(_cndnModal);
  const [print, setPrint] = useState<IDataModal<ICndn>>({
    ..._printModal,
  });
  const onDeleteCndn = async () => {
    try {
      await deleteCndn(Number(deleteModal?.id));
      notification.success({
        message: 'ยกเลิกใบเพิ่มหนี้/ลดหนี้สำเร็จ',
      });
      refetch();
      setDeleteModal({ ..._cndnModal });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Card>
      <BreadcrumbComponent
        items={[
          {
            title: (
              <div
                className="cursor-pointer"
                onClick={() => navigate(`/cndn-note`)}
              >
                {`ใบ${cndn && getCndnTypeName(cndn?.type)}`}
              </div>
            ),
          },
          {
            title: `${cndn?.code}`,
          },
        ]}
      />
      {order && cndn && (
        <>
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`ออเดอร์`}
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-3 border p-2 py-4 rounded-lg">
            <DetailItem label="เลขที่ออเดอร์ (OR)" detail={order?.code} />
            <DetailItem
              label="วันที่สั่งซื้อ"
              detail={formatDate(order?.orderCreateDate)}
            />
            <DetailItem
              label="วันที่ส่งสินค้า"
              detail={formatDate(order?.orderDate)}
            />
            <DetailItem
              label="วันที่ครบกำหนด"
              detail={`${formatDate(order?.dueDate)} (${order?.creditTerm || 0} วัน)`}
            />
            <DetailItem label="เลขที่ใบสั่งสินค้า (CO)" detail={order?.soNo} />
            <DetailItem label="เลขที่ใบแจ้งหนี้ (IV)" detail={order?.ivNo} />
            <DetailItem label="เลขที่ใบสั่งซื้อ (PO)" detail={order?.poNo} />
            <DetailItem
              label="รหัสลูกค้า"
              customClass="text-blue-700"
              detail={order.customerCode}
            />
            <DetailItem
              label="ลูกค้า"
              customClass="text-blue-700"
              detail={order.customerName}
            />
            <DetailItem label="BU" detail={order?.businessUnitName} />
            <DetailItem
              label="ที่อยู่ลูกค้า"
              detail={getFullAddress(order.customer)}
            />
            <DetailItem
              label="เบอร์โทรศัพท์"
              detail={order?.customer?.phoneNumber}
            />
            <DetailItem
              label="พนักงานขาย"
              customClass="text-blue-700"
              detail={order.salesName}
            />
            <DetailItem
              label="ประเภทออเดอร์"
              detail={order?.isSample ? 'ตัวอย่าง' : 'สินค้าขาย'}
              customClass="bg-yellow-500 p-0.5 rounded px-2 text-black"
            />
            <DetailItem label="หมายเหตุ" detail={order?.remark} />
            <DetailItem
              label="หมายเหตุเลื่อนส่ง"
              detail={order?.postponeRemark}
              customClass="bg-yellow-500 p-0.5 rounded px-2 text-black"
            />
            <DetailItem
              label="สาเหตุที่ออกใบลดหนี้"
              detail={cndn.remark}
              customClass="bg-yellow-500 p-0.5 rounded px-2 text-black"
            />
          </div>
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`รายการตามใบแจ้งหนี้`}
          />
          <OrderItemTable
            order={order}
            orderItems={order.orderItems || []}
            showFooter={false}
          />
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`รายการใบ${cndn && getCndnTypeName(cndn?.type)}`}
          />
          {cndn && <CndnItemTable cndn={cndn} />}
          <Divider className="" />
          <div className="flex flex-row justify-between">
            <Button
              type="default"
              size="large"
              className="border-red-500 text-red-700"
              disabled={!cndn?.isActive}
              onClick={() =>
                setDeleteModal({
                  open: true,
                  item: cndn,
                  id: cndn?.id,
                })
              }
            >
              ยกเลิกเอกสาร
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() =>
                setPrint({
                  open: true,
                  id: 'TM26',
                })
              }
            >
              <PrinterOutlined />
              พิมพ์เอกสาร
            </Button>
          </div>
          <Suspense>
            <DeleteConfirmModal
              content={`คุณต้องการลบ '${deleteModal?.item?.code}' ใช่หรือไม่`}
              handleCancel={() => {
                setDeleteModal({ ..._cndnModal });
              }}
              handleOk={() => {
                onDeleteCndn();
              }}
              open={deleteModal?.open}
              title={`ใบเพิ่มหนี้/ลดหนี้ '${deleteModal?.item?.code}'`}
            />
            <PrintCndnModal
              open={print.open}
              handleCancel={function (): void {
                setPrint({
                  ..._printModal,
                });
              }}
              order={order}
              cndn={cndn}
              tms={[print.id as string]}
            />
          </Suspense>
        </>
      )}
    </Card>
  );
};

export default ViewCndn;
